import React, { useEffect, useRef, useState } from "react";
import Icon from "./icons";
import Avatar from "./avatar";
import { contextChipLabel } from "../../../lib/artemis/present";

// The bottom of the panel: what Artemis can see and search, the question box, and Send, which
// turns into Stop while a reply runs. The chips replace the old always-on source checkboxes.

const Popover = ({ title, onClose, children }) => {
  const ref = useRef(null)
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    const onDown = e => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest('.artemis-chip')) onClose() }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [onClose])
  return (
    <div className="artemis-popover" role="dialog" aria-label={title} ref={ref}>
      <span className="artemis-eyebrow">{title}</span>
      {children}
    </div>
  )
}

const Composer = ({
  input, onInput, onSend, onStop, working, hasChat, answering,
  view,
  models, selectedModel, onSelectModel, about, dictation, speaking, onStopSpeaking,
  mentionOptions = null, groupPlaceholder = null, placeholder = null
}) => {
  // 'context' or null.
  const [menu, setMenu] = useState(null)
  const close = () => setMenu(null)
  const toggle = name => setMenu(m => (m === name ? null : name))
  const canSend = !working && !!input.trim()

  // Group chats: typing @ offers Artemis and the members; Enter or a click puts "@Name " in the box.
  const box = useRef(null)
  const typed = mentionOptions ? (input.match(/(?:^|\s)@(\w*)$/) || null) : null
  const matches = typed ? mentionOptions.filter(o => o.token.toLowerCase().startsWith(typed[1].toLowerCase())) : []
  const pickMention = option => {
    onInput(input.replace(/@\w*$/, `@${option.token} `))
    if (box.current) box.current.focus()
  }
  const insertAt = () => {
    onInput(input + (input && !/\s$/.test(input) ? ' @' : '@'))
    if (box.current) box.current.focus()
  }

  const onKeyDown = e => {
    if (matches.length && (e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
      e.preventDefault()
      pickMention(matches[0])
      return
    }
    if (matches.length && e.key === 'Escape') {
      e.preventDefault()
      onInput(input.replace(/@\w*$/, ''))
      return
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (canSend) onSend()
    }
  }

  return (
    <div className="artemis-composer">
      {menu === 'context' && (
        <Popover title="What Artemis can see" onClose={close}>
          {view.rows.map(row => (
            <div key={row.k} className="artemis-context-row"><span>{row.k}</span><span>{row.v}</span></div>
          ))}
        </Popover>
      )}

      <div className="artemis-chip-row artemis-composer-chips">
        {view.available && (
          <button type="button" className={`artemis-chip ${menu === 'context' ? 'artemis-chip-open' : ''}`} aria-expanded={menu === 'context'}
            onClick={() => { if (menu !== 'context') view.refresh(); toggle('context') }}>
            <span className="artemis-chip-live"><Icon name="eye" size={15} /></span>
            <span className="artemis-chip-text">{contextChipLabel(view.basemapLayerId, view.overlayCount, view.onScratchpad)}</span>
          </button>
        )}
        {models.length > 0 && (
          <select className="artemis-model-select" aria-label="Model" value={selectedModel || ''} onChange={e => onSelectModel(e.target.value)}>
            {models.map(m => <option key={m.key} value={m.key}>{m.displayName}</option>)}
          </select>
        )}
      </div>

      {about && (
        // The waypoint or shape open on the map goes with the question until it's dropped.
        <div className="artemis-about" role="status">
          <Icon name="pin" size={15} />
          <span className="artemis-chip-text">About: {about.name}</span>
          <button type="button" className="artemis-about-drop" title="Don't ask about this" aria-label={`Don't ask about ${about.name}`} onClick={about.onDrop}>
            <Icon name="close" size={14} />
          </button>
        </div>
      )}

      {speaking && (
        <div className="artemis-speaking" role="status">
          <span className="artemis-speaking-wave" aria-hidden="true"><span /><span /><span /></span>
          <span className="artemis-flex">Speaking…</span>
          {/* Not "Stop": the reply's own Stop sits just below. */}
          <button type="button" className="artemis-small-button" onClick={onStopSpeaking}>Stop reading</button>
        </div>
      )}
      {dictation && dictation.error && (
        <div className="artemis-dictation-note" role="status">
          {dictation.error === 'denied' ? 'The microphone is blocked for this site. Allow it in your browser to talk to Artemis.'
            : dictation.error === 'busy' ? 'Too many voice questions in a minute. Wait a moment and try again.'
              : 'Couldn\u2019t hear that. Try again.'}
        </div>
      )}

      {matches.length > 0 && (
        <div className="artemis-mention-menu" role="listbox" aria-label="Mention">
          <span className="artemis-eyebrow">Mention</span>
          {matches.map((o, i) => (
            <button key={o.key} type="button" role="option" aria-selected={i === 0} className="artemis-mention-option" onMouseDown={e => { e.preventDefault(); pickMention(o) }}>
              {o.ai
                ? <span className="artemis-avatar artemis-avatar-ai"><Icon name="sparkle" size={13} /></span>
                : <Avatar url={o.avatarUrl} initials={o.initials} name={o.label} />}
              <span className="artemis-flex">{o.label}</span>
              <span className="artemis-mention-hint">{o.hint}</span>
            </button>
          ))}
        </div>
      )}

      <div className="artemis-input-box">
        {mentionOptions && (
          <button type="button" className="artemis-at-button" title="Mention someone" aria-label="Mention someone" onClick={insertAt}>@</button>
        )}
        <textarea
          ref={box}
          className="chat-inputfield artemis-input"
          rows={2}
          aria-label="Ask Artemis"
          placeholder={dictation && dictation.busy ? 'Transcribing…' : dictation && dictation.listening ? 'Listening… (pause or tap the mic to finish)' : working ? 'Ask a follow-up once this reply is done…' : answering ? 'Or type your answer…' : groupPlaceholder || placeholder || (hasChat ? 'Ask a follow-up…' : 'Ask about hunting or your map…')}
          value={input}
          onChange={e => onInput(e.target.value)}
          onKeyDown={onKeyDown}
        />
        {dictation && dictation.supported && !working && (
          <button type="button" className={`artemis-mic-button ${dictation.listening ? 'artemis-mic-on' : ''} ${dictation.busy ? 'artemis-mic-busy' : ''}`}
            title={dictation.busy ? 'Transcribing…' : dictation.listening ? 'Stop listening' : 'Talk to Artemis'}
            aria-label={dictation.busy ? 'Transcribing' : dictation.listening ? 'Stop listening' : 'Talk to Artemis'}
            aria-pressed={dictation.listening} disabled={dictation.busy} onClick={dictation.listening ? dictation.stop : dictation.start}>
            {dictation.busy ? <span className="artemis-step-spinner artemis-mic-spinner" aria-hidden="true" /> : <Icon name={dictation.listening ? 'micOn' : 'mic'} size={20} />}
          </button>
        )}
        {working ? (
          <button type="button" className="artemis-stop-button" title="Stop Artemis" onClick={onStop}>
            <Icon name="stop" size={15} />Stop
          </button>
        ) : (
          <button type="button" className={`enter-button artemis-send-button ${canSend ? 'artemis-send-ready' : ''}`}
            title="Send" aria-label="Send" disabled={!canSend} onClick={onSend}>
            <Icon name="send" size={20} />
          </button>
        )}
      </div>
    </div>
  )
}

export default Composer
